/**
 * Fetch wrapper that injects the JWT from localStorage and bubbles up
 * structured errors. On 401 it clears the token and reloads so the user
 * is bounced to the login page — a silent re-auth loop would mask real
 * credential rot.
 *
 * Base URL is always relative so the same bundle works in dev (vite proxy
 * rewrites /api → http://localhost:8801) and in prod (nginx serves the
 * bundle and proxies /api to cloud_portal on the same origin).
 */
const TOKEN_KEY = "cloud_portal_jwt";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const resp = await fetch(`/api${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await resp.text();
  const data = text ? safeJson(text) : null;

  if (!resp.ok) {
    // Auto-logout on 401 — don't paper over it, let the UI re-render
    // logged-out state. 403 is kept (e.g., non-admin hitting /owners) so
    // the user sees "requires platform admin" instead of being logged out.
    if (resp.status === 401) {
      clearToken();
      // Hard reload to reset all state + redirect to login
      window.location.href = "/login";
    }
    const detail =
      (data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : undefined) || resp.statusText;
    throw new ApiError(resp.status, detail, data);
  }

  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

// ── Typed shapes (mirrors cloud_portal/app/schemas) ────────────────

export interface User {
  id: string;
  owner_id: string;
  email: string;
  role: "owner" | "manager" | "viewer";
  is_platform_admin: boolean;
}

export interface Token {
  access_token: string;
  token_type: string;
  user_id: string;
  owner_id: string;
  role: string;
}

export interface Farm {
  id: string;
  owner_id: string;
  slug: string;
  name: string;
  timezone: string;
  lat: number | null;
  lon: number | null;
  notes: string;
  created_at: string;
}

export interface Master {
  id: string;
  owner_id: string;
  farm_id: string;
  label: string;
  hardware_mac: string;
  mqtt_username: string;
  last_seen_at: string | null;
  /** Non-null ⇒ master_backend's reverse-WS tunnel is connected right now.
   *  Use this (not last_seen_at) for the "online" badge — last_seen_at gets
   *  bumped by ANY MQTT traffic on the master's tenant topic, including
   *  LoRaWAN sensor uplinks via tenant_bridge, so it can stay fresh even
   *  when the master_backend is down. */
  tunnel_connected_at: string | null;
  created_at: string;
  disabled_at: string | null;
}

export interface MasterCreateOut extends Master {
  api_key: string;
  bridge_config: string;
}

export interface Owner {
  id: string;
  slug: string;
  email: string;
  display_name: string;
  plan: string;
  is_platform_admin: boolean;
  created_at: string;
  disabled_at: string | null;
}

export interface TenantUser {
  id: string;
  owner_id: string;
  email: string;
  role: "owner" | "manager" | "viewer";
  created_at: string;
  disabled_at: string | null;
}

// ── Operational state (replicated from master_backend via MQTT) ────

export interface ZoneState {
  id: number;
  name: string;
  icon: string;
  status: "idle" | "running" | "suspended" | "error" | "scheduled";
  watering_duration_minutes: number;
  watering_type: string;
  valve_ids: number[];
  pump_ids: number[];
  polygon: unknown;
  cycle_soak_enabled: boolean;
  master_id: string;     // injected by cloud_portal from outer key
  snapshot_ts: number;   // unix ts when ingest saw the state snapshot
}

export interface ActiveZone {
  master_id: string;
  zone_id: number;
  source: string | null;
  source_name: string | null;
}

export interface ZonesResponse {
  zones: ZoneState[];
  active: ActiveZone[];
  queue_length: number;
}

export interface SensorState {
  id: number;
  name: string;
  sensor_type: string;
  value: number | null;
  unit: string;
  status: string;
  source: string;
  node_id: string | null;
  last_updated: string | null;
  battery_level: number | null;
  master_id: string;
  snapshot_ts: number;
}

export interface ControllerState {
  id: number;
  name: string;
  role: string;
  status: string;
  ip_address: string;
  last_seen_at: string | null;
  master_id: string;
  snapshot_ts: number;
}

export interface LorawanDevice {
  dev_eui: string;
  name: string;
  description: string;
  master_id: string;
  last_seen_at: string | null;
  /** 'A' / 'B' / 'C' from the ChirpStack DeviceProfile, or '?' if the
   *  profile fetch failed. Most farm battery sensors are Class A. */
  device_class: string | null;
  /** Has the device been heard within the last 24h. Driven by ChirpStack's
   *  last_seen timestamp, NOT by lorawan_tenant_bridge — same source the
   *  cloud admin sees in the ChirpStack dashboard. */
  is_online: boolean;
  /** Operator-set kill switch on the cloud side. ChirpStack drops uplinks
   *  from disabled devices but keeps session/keys, so re-enabling is
   *  instant (no re-join). */
  is_disabled: boolean;
}

export interface LorawanDeviceCreate {
  dev_eui: string;
  app_key: string;
  name: string;
  master_id: string;
}
